// src/pages/ReviewFile.jsx
// Review (ISRE 2400 Revised) engagement file. Sits between Review and Statements for a client
// whose engagement type is "review". The server decides what is complete and which conclusion
// is permitted (backend/review_engagement.py); this page records the work and shows the result.
// Statements cannot generate a reviewed AFS until the file is approved by a second person.

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  getReviewFileMeta, getReviewFile, saveReviewSection, refreshReviewAnalytics,
  submitReviewFile, decideReviewFile, getCreditSummary,
} from '../api/kailux'
import { useAuth } from '../context/AuthContext'
import BackButton from '../components/BackButton'
import Icon, { Dot } from '../components/Icon'
import { T, alpha } from '../theme/theme'
import {
  FIELDS, SECTION_INTRO, INQUIRY_ITEM_FIELDS, MISSTATEMENT_ITEM_FIELDS,
  MISSTATEMENT_SECTION_FIELDS, ANALYTICAL_CONCLUSIONS,
} from '../constants/reviewFileSchema'

const STATUS_LABEL = { in_progress: 'In progress', submitted: 'Awaiting approval', approved: 'Approved' }
const STATUS_COLOR = { in_progress: T.am, submitted: T.bl, approved: T.gn }

const apiMessage = (err, fallback) => {
  const d = err?.response?.data?.detail
  if (!d) return fallback
  if (typeof d === 'string') return d
  const list = Array.isArray(d.blockers) ? d.blockers.map(b => b.message) : []
  return [d.message || fallback, ...list.slice(0, 5)].join(' ')
}

const input = {
  width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8,
  border: `1px solid ${T.bd}`, background: T.s2, color: T.tx, fontFamily: 'inherit', fontSize: 14,
}
const label = { display: 'block', fontSize: 13, color: T.lt, margin: '14px 0 5px' }
const btn = (primary, disabled) => ({
  padding: '9px 16px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
  fontSize: 14, fontWeight: 600, border: primary ? 'none' : `1px solid ${T.bd}`,
  background: disabled ? T.mt : primary ? T.prg : 'transparent',
  color: primary || disabled ? T.onpri : T.tx,
})

function Field({ f, value, onChange, topics }) {
  const common = { id: `rf-${f.k}`, value: value ?? '', onChange: e => onChange(f.k, e.target.value) }
  if (f.kind === 'check') {
    return (
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '12px 0', color: T.tx, fontSize: 14 }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(f.k, e.target.checked)} style={{ marginTop: 3 }} />
        <span>{f.label}</span>
      </label>
    )
  }
  const options = f.kind === 'topic' ? (topics || []).map(t => [t.id, t.label]) : f.options
  return (
    <div>
      <label htmlFor={`rf-${f.k}`} style={label}>{f.label}</label>
      {f.kind === 'textarea' && <textarea {...common} rows={4} style={{ ...input, resize: 'vertical' }} />}
      {(f.kind === 'text') && <input {...common} type="text" style={input} />}
      {f.kind === 'number' && <input {...common} type="number" style={input} />}
      {f.kind === 'date' && <input {...common} type="date" style={input} />}
      {(f.kind === 'select' || f.kind === 'topic') && (
        <select {...common} style={input}>
          <option value="">Select…</option>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      )}
    </div>
  )
}

function Fields({ fields, data, onChange, topics }) {
  return fields.filter(f => !f.showIf || f.showIf(data)).map(f => (
    <Field key={f.k} f={f} value={data[f.k]} onChange={onChange} topics={topics} />
  ))
}

const newId = () => 'x' + Math.random().toString(36).slice(2, 10)

export default function ReviewFile() {
  const navigate = useNavigate()
  const location = useLocation()
  const { firm } = useAuth()
  const state = location.state || {}
  const client = state.client
  const financialYear = String(state.financial_year || sessionStorage.getItem('kailux:financial_year') || '')
  const figures = state.figures || {}
  const priorFigures = state.prior_figures || {}

  const [meta, setMeta] = useState(null)
  const [file, setFile] = useState(null)
  const [section, setSection] = useState('acceptance')
  const [draft, setDraft] = useState({})
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [selfReason, setSelfReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [credit, setCredit] = useState(null)
  const isPrepaid = firm?.plan_type === 'pay_per_engagement'

  useEffect(() => {
    if (!isPrepaid) return
    let live = true
    getCreditSummary().then(r => { if (live) setCredit(r.data) }).catch(() => {})
    return () => { live = false }
  }, [isPrepaid])

  const adopt = useCallback((view) => {
    setFile(view)
    setDraft(view.data)
    setDirty(false)
  }, [])

  useEffect(() => {
    if (!client?.id || !financialYear) { setLoading(false); return }
    let live = true
    ;(async () => {
      try {
        const [m, v] = await Promise.all([getReviewFileMeta(), getReviewFile(client.id, financialYear)])
        if (!live) return
        setMeta(m)
        adopt(v)
      } catch (err) {
        if (live) setError(apiMessage(err, 'Could not load the review file.'))
      } finally {
        if (live) setLoading(false)
      }
    })()
    return () => { live = false }
  }, [client?.id, financialYear, adopt])

  const setField = (k, v) => { setDraft(d => ({ ...d, [section]: { ...d[section], [k]: v } })); setDirty(true) }
  const setSectionData = (patch) => { setDraft(d => ({ ...d, [section]: { ...d[section], ...patch } })); setDirty(true) }

  const run = async (fn, okMessage) => {
    setBusy(true); setError(''); setNotice('')
    try {
      const view = await fn()
      adopt(view)
      if (okMessage) setNotice(okMessage + (view.reopened ? ' The earlier approval was voided because the file changed.' : ''))
      return view
    } catch (err) {
      setError(apiMessage(err, 'That did not work. Please try again.'))
      return null
    } finally {
      setBusy(false)
    }
  }

  const save = () => run(() => saveReviewSection({
    client_id: client.id, financial_year: financialYear, section, data: draft[section],
  }), 'Saved.')

  const switchSection = async (id) => {
    if (dirty && file?.status !== 'approved') {
      const v = await run(() => saveReviewSection({
        client_id: client.id, financial_year: financialYear, section, data: draft[section] }))
      if (!v) return
    }
    setSection(id)
  }

  const refresh = () => run(() => refreshReviewAnalytics({
    client_id: client.id, financial_year: financialYear, figures, prior_figures: priorFigures,
  }), 'Analytical procedures refreshed from the current figures.')

  const submit = async () => {
    if (dirty) { const v = await save(); if (!v) return }
    await run(() => submitReviewFile({ client_id: client.id, financial_year: financialYear }), 'Submitted for approval.')
  }

  const decide = (decision) => run(() => decideReviewFile({
    client_id: client.id, financial_year: financialYear, decision, self_review_reason: selfReason,
  }), decision === 'approved' ? 'Approved.' : 'Returned for changes.')

  const goToStatements = () => navigate('/statements', { state })

  if (!client?.id || !financialYear) {
    return (
      <div style={{ padding: 32, color: T.tx, background: T.bg, minHeight: '100vh' }}>
        <p>Open the review file from a client's AFS, after the Review step.</p>
        <button style={btn(false)} onClick={() => navigate('/dashboard')}>Back to dashboard</button>
      </div>
    )
  }
  if (loading) return <div style={{ padding: 32, color: T.mt, background: T.bg, minHeight: '100vh' }}>Loading the review file…</div>
  if (!file || !meta) {
    return (
      <div style={{ padding: 32, background: T.bg, minHeight: '100vh', color: T.tx }}>
        <p role="alert" style={{ color: T.rd }}>{error || 'The review file is not available.'}</p>
        <button style={btn(false)} onClick={() => navigate(-1)}>Back</button>
      </div>
    )
  }

  const ev = file.evaluation
  const locked = file.status !== 'in_progress'
  const sectionBlockers = ev.blockers.filter(b => b.section === section)
  const sectionData = draft[section] || {}
  const topics = meta.inquiry_topics

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.tx, fontFamily: "'Outfit',sans-serif" }}>
      <header style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '14px 24px', borderBottom: `1px solid ${T.bd}`, flexWrap: 'wrap' }}>
        <BackButton onClick={() => navigate('/review', { state })} label="Back to review" />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>Review file: {client.name} {financialYear}</div>
          <div style={{ color: T.mt, fontSize: 13 }}>ISRE 2400 (Revised): limited assurance engagement</div>
        </div>
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', color: STATUS_COLOR[file.status], fontWeight: 600 }}>
          <Dot color={STATUS_COLOR[file.status]} /> {STATUS_LABEL[file.status]}
        </span>
      </header>

      {isPrepaid && credit && client?.id && (() => {
        const reviewTaken = (credit.ledger || []).some(m =>
          m.credit_type === 'review' && Number(m.quantity) < 0 &&
          m.client_id === client.id && String(m.financial_year) === financialYear)
        const held = credit.credits || {}
        const needReview = reviewTaken ? 0 : 1
        const short = Number(held.afs || 0) < 1 || Number(held.review || 0) < needReview
        const needs = needReview ? '1 AFS credit and 1 Review credit' : '1 AFS credit'
        return (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '10px 24px',
            borderBottom: `1px solid ${T.bd}`, background: short ? alpha(T.am, 0.12) : 'transparent', fontSize: 13 }}>
            <span style={{ color: short ? T.am : T.mt }}>
              {`This review needs ${needs}. You have ${Number(held.afs || 0)} AFS and ${Number(held.review || 0)} Review.`}
            </span>
            {short && <button style={btn(true)} onClick={() => navigate('/billing')}>Buy credits</button>}
          </div>
        )
      })()}

      <div style={{ display: 'flex', gap: 24, padding: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <nav aria-label="Review file sections" style={{ width: 250, flexShrink: 0 }}>
          {meta.sections.map(s => {
            const done = ev.section_status[s.id] === 'complete'
            const active = s.id === section
            return (
              <button key={s.id} onClick={() => switchSection(s.id)} aria-current={active ? 'true' : undefined}
                style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '9px 12px',
                  marginBottom: 4, borderRadius: 8, border: `1px solid ${active ? T.or : 'transparent'}`,
                  background: active ? T.ord : 'transparent', color: T.tx, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 }}>
                <span style={{ color: done ? T.gn : T.mt }}><Icon name={done ? 'checkCircle' : 'circle'} /></span>
                {s.title}
              </button>
            )
          })}
          <div style={{ marginTop: 14, color: T.mt, fontSize: 13 }}>
            {ev.blockers.length === 0 ? 'Every section is complete.' : `${ev.blockers.length} item(s) still open.`}
          </div>
        </nav>

        <main style={{ flex: 1, minWidth: 300, maxWidth: 820 }}>
          <h2 style={{ marginTop: 0 }}>{meta.sections.find(s => s.id === section)?.title}</h2>
          <p style={{ color: T.mt, marginTop: 0 }}>{SECTION_INTRO[section]}</p>

          {error && <div role="alert" style={{ padding: 12, borderRadius: 8, background: T.rdd, color: T.rd, marginBottom: 12 }}>{error}</div>}
          {notice && <div role="status" style={{ padding: 12, borderRadius: 8, background: T.gnd, color: T.gn, marginBottom: 12 }}>{notice}</div>}
          {locked && (
            <div style={{ padding: 12, borderRadius: 8, background: T.bld, color: T.bl, marginBottom: 12 }}>
              <Icon name="lock" /> This file is {STATUS_LABEL[file.status].toLowerCase()}. Changing anything reopens it and voids the approval.
            </div>
          )}
          {sectionBlockers.length > 0 && (
            <ul aria-label="Open items" style={{ padding: '10px 10px 10px 28px', borderRadius: 8, background: T.amd, color: T.am, margin: '0 0 12px' }}>
              {sectionBlockers.map((b, i) => <li key={b.code + i}>{b.message}</li>)}
            </ul>
          )}

          <div style={{ background: T.s2, border: `1px solid ${T.bd}`, borderRadius: 12, padding: '4px 20px 20px', boxShadow: T.sh }}>
            {section === 'analytical' ? (
              <AnalyticalPanel data={sectionData} onChange={setSectionData} onRefresh={refresh} busy={busy} materiality={draft.materiality?.overall} />
            ) : section === 'inquiries' ? (
              <ListPanel items={sectionData.items || []} fields={INQUIRY_ITEM_FIELDS} topics={topics} addLabel="Add inquiry"
                blank={{ topic: 'fraud' }} onChange={items => setSectionData({ items })} />
            ) : section === 'misstatements' ? (
              <>
                <ListPanel items={sectionData.items || []} fields={MISSTATEMENT_ITEM_FIELDS} addLabel="Add misstatement"
                  blank={{ kind: 'factual', status: 'uncorrected' }} onChange={items => setSectionData({ items })} />
                <p style={{ color: T.mt, fontSize: 13 }}>Uncorrected, not clearly trivial: R {Number(ev.uncorrected_total).toLocaleString()}</p>
                <Fields fields={MISSTATEMENT_SECTION_FIELDS} data={sectionData} onChange={setField} />
              </>
            ) : (
              <>
                {section === 'conclusion' && (
                  <p style={{ color: T.mt, fontSize: 13 }}>
                    Permitted on this file: {ev.allowed_conclusions.map(t => meta.conclusion_types.find(c => c.id === t)?.label).join(', ')}.
                    {ev.modification_reasons.map(r => <span key={r.code} style={{ display: 'block' }}>{r.message}</span>)}
                  </p>
                )}
                <Fields fields={FIELDS[section] || []} data={sectionData} onChange={setField} />
              </>
            )}
            {ev.warnings.filter(w => w.section === section).map(w => (
              <p key={w.code} style={{ color: T.am, fontSize: 13 }}><Icon name="alert" /> {w.message}</p>
            ))}
            <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
              <button style={btn(true, busy || !dirty)} disabled={busy || !dirty} onClick={save}>Save section</button>
            </div>
          </div>

          <div style={{ marginTop: 24, background: T.s2, border: `1px solid ${T.bd}`, borderRadius: 12, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Approval</h3>
            {file.status === 'in_progress' && (
              <>
                <p style={{ color: T.mt, marginTop: 0 }}>When every section is complete, submit the file. A second person must approve it before a reviewed AFS can be generated.</p>
                <button style={btn(true, busy || !ev.complete)} disabled={busy || !ev.complete} onClick={submit}>Submit for approval</button>
              </>
            )}
            {file.status === 'submitted' && (
              <>
                <p style={{ color: T.mt, marginTop: 0 }}>Submitted by {file.submitted_by_email || 'a team member'}.</p>
                {!file.can_approve && <p style={{ color: T.am }}>You do not have permission to approve review files.</p>}
                {file.can_approve && file.caller_is_submitter && !file.is_sole_user && (
                  <p style={{ color: T.am }}>You submitted this file, so a different person must approve it.</p>
                )}
                {file.can_approve && file.caller_is_submitter && file.is_sole_user && (
                  <>
                    <p style={{ color: T.am }}>You are the only user in this firm. Record why a second review is not possible.</p>
                    <textarea aria-label="Why a second review is not possible" rows={3} value={selfReason}
                      onChange={e => setSelfReason(e.target.value)} style={{ ...input, resize: 'vertical' }} />
                  </>
                )}
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button style={btn(true, busy || !file.can_approve || (file.caller_is_submitter && !file.is_sole_user))}
                    disabled={busy || !file.can_approve || (file.caller_is_submitter && !file.is_sole_user)} onClick={() => decide('approved')}>Approve</button>
                  <button style={btn(false, busy || !file.can_approve)} disabled={busy || !file.can_approve} onClick={() => decide('changes_requested')}>Return for changes</button>
                </div>
              </>
            )}
            {file.status === 'approved' && (
              <>
                <p style={{ color: T.gn, marginTop: 0 }}>
                  <Icon name="checkCircle" /> Approved by {file.approved_by_email || 'a reviewer'}.
                </p>
                {file.self_review_reason && <p style={{ color: T.mt }}>Own file, reason recorded: {file.self_review_reason}</p>}
                <button style={btn(true, busy)} disabled={busy} onClick={goToStatements}>Continue to financial statements</button>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

function ListPanel({ items, fields, topics, addLabel, blank, onChange }) {
  const update = (i, k, v) => onChange(items.map((it, n) => (n === i ? { ...it, [k]: v } : it)))
  return (
    <div>
      {items.map((it, i) => (
        <div key={it.id || i} style={{ borderBottom: `1px solid ${T.bd}`, paddingBottom: 12 }}>
          <Fields fields={fields} data={it} onChange={(k, v) => update(i, k, v)} topics={topics} />
          <button style={btn(false)} onClick={() => onChange(items.filter((_, n) => n !== i))}>Remove</button>
        </div>
      ))}
      <button style={{ ...btn(false), marginTop: 14 }} onClick={() => onChange([...items, { id: newId(), ...blank }])}>{addLabel}</button>
    </div>
  )
}

function AnalyticalPanel({ data, onChange, onRefresh, busy, materiality }) {
  const rows = data.rows || []
  const update = (i, k, v) => onChange({ rows: rows.map((r, n) => (n === i ? { ...r, [k]: v } : r)) })
  const fmt = v => (v === null || v === undefined ? 'n/a' : Number(v).toLocaleString())
  return (
    <div>
      <div style={{ margin: '14px 0' }}>
        <button style={btn(true, busy)} disabled={busy} onClick={onRefresh}><Icon name="refresh" /> Refresh from current figures</button>
        <span style={{ color: T.mt, fontSize: 13, marginLeft: 12 }}>
          {materiality ? `Flags movements of 10% or more and at least half of materiality (R ${Number(materiality / 2).toLocaleString()}).` : 'Set materiality first for sharper flags.'}
        </span>
      </div>
      {rows.length === 0 && <p style={{ color: T.mt }}>No analytical rows yet. Refresh from the current figures.</p>}
      {rows.map((r, i) => (
        <div key={r.id || i} style={{ borderTop: `1px solid ${T.bd}`, padding: '10px 0' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong>{r.area}</strong>
            <span style={{ color: T.mt, fontSize: 13 }}>
              Current {fmt(r.current)} | Prior {fmt(r.prior)} | Change {fmt(r.change)}{r.change_pct !== null && r.change_pct !== undefined ? ` (${r.change_pct}%)` : ''}
            </span>
            {r.flagged && <span style={{ color: T.am, background: T.amd, borderRadius: 6, padding: '1px 8px', fontSize: 12 }}>Flagged</span>}
          </div>
          {(r.flagged || r.expectation || r.explanation || r.conclusion) && (
            <>
              <Field f={{ k: 'explanation', kind: 'textarea', label: 'Explanation from management' }} value={r.explanation} onChange={(k, v) => update(i, k, v)} />
              <Field f={{ k: 'corroboration', kind: 'textarea', label: 'How it was corroborated' }} value={r.corroboration} onChange={(k, v) => update(i, k, v)} />
            </>
          )}
          <Field f={{ k: 'conclusion', kind: 'select', label: 'Conclusion', options: ANALYTICAL_CONCLUSIONS }} value={r.conclusion} onChange={(k, v) => update(i, k, v)} />
        </div>
      ))}
      <Field f={{ k: 'overall_conclusion', kind: 'textarea', label: 'Overall conclusion on the analytical procedures' }}
        value={data.overall_conclusion} onChange={(k, v) => onChange({ [k]: v })} />
    </div>
  )
}
